import React, { FC } from 'react';
import mixpanel from 'Jsx/Utils/mixpanelTracking';
import { useQuery } from '@apollo/react-hooks';

import Loader from 'crehana-ui/Loader';

import { useTranslation } from 'Shared/Hooks/useTranslation';
import BoldLock from 'crehana-icons/BoldLock';
import BoldLockOpen from 'crehana-icons/BoldLockOpen';
import { secondaryMain, grayDark } from 'crehana-ui/styles/colors';
import { UserType } from 'Jsx/Types/UserType';
import { Course } from '../../types';

import * as EVENTS from '../../events';

import GET_MODULE_SET_QUERY from './graphql/getModuleSet.graphql';

import LessonModal from './LessonModal';
import ToggleItem from './ToggleItem';
import locales from '../../i18n/localeEs';

const translations = locales.es;

export interface CourseModuleSet {
  edges?: EdgesEntity[] | null;
  __typename: string;
}
export interface EdgesEntity {
  node: Module;
  __typename: string;
}
export interface Module {
  originalId: number;
  order: number;
  name: string;
  duration: number;
  videoLectureSet: VideoLectureSet;
  __typename: string;
}
export interface VideoLectureSet {
  edges?: EdgesEntity1[] | null;
  __typename: string;
}
export interface EdgesEntity1 {
  node: VideoLecture;
  __typename: string;
}
export interface VideoLecture {
  originalId: number;
  order: number;
  title: string;
  duration: number;
  __typename: string;
}

interface LessonsSectionProps {
  mounted: boolean;
  course: Course;
  user: UserType;
  BuyButtons: any;
}

const LessonsSection: FC<LessonsSectionProps> = ({
  mounted,
  course,
  user,
  BuyButtons,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const [modules, setModules] = React.useState<Module[]>([]);
  const { data, loading, error } = useQuery<
    {
      course: {
        courseModuleSet: CourseModuleSet;
      };
    },
    { id: string }
  >(GET_MODULE_SET_QUERY, {
    variables: { id: course.originalId.toString() },
  });

  React.useEffect(() => {
    if (data && !loading && !error) {
      setModules(data?.course?.courseModuleSet?.edges?.map(el => el.node));
    }
  }, [data, loading, error]);

  let firstLessonNumber = 0;

  const handleLessonClick = () => {
    if (user) {
      if (!user.hasOrganization) {
        mixpanel.track(EVENTS.CLICK_ON_LESSON, {});
        setOpen(true);
      }
    } else {
      window.showLoginModal();
    }
  };

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center py-32">
        <Loader size={40} />
      </div>
    );
  }

  return (
    <section className="mb-32 lg:mt-40">
      <h2 className="font-h3 mb-24">
        {t('LESSONS_SECTION_TITLE', {
          defaultValue: translations.LESSONS_SECTION_TITLE,
        })}
      </h2>
      <div className="relative">
        {modules.map((module, indexChil) => (
          <ToggleItem
            indexChildren={indexChil}
            title={module.name}
            key={`module${module.originalId}`}
            className="mb-16"
          >
            <ul>
              {module?.videoLectureSet?.edges
                .map(el => el.node)
                .map(lesson => {
                  firstLessonNumber += 1;
                  return (
                    <li
                      key={`lessons-${lesson.originalId}`}
                      className="mb-8"
                      data-cy="course-lesson"
                    >
                      <button
                        type="button"
                        className="block w-full cursor-pointer flex items-center text-left focus:outline-none"
                        onClick={handleLessonClick}
                      >
                        <p className="truncate font-body2">
                          {firstLessonNumber}. {lesson.title}
                        </p>
                        <div className="flex-shrink-0 ml-auto pl-12">
                          {course.courseType === 'free' ? (
                            <BoldLockOpen size={20} color={secondaryMain} />
                          ) : (
                            <BoldLock size={20} color={grayDark} />
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
            </ul>
          </ToggleItem>
        ))}
      </div>

      <LessonModal
        lessonModal={open}
        hideModal={() => {
          setOpen(false);
        }}
        course={course}
        user={user}
        BuyButtons={BuyButtons}
      />
    </section>
  );
};

export default LessonsSection;
